import { useState } from 'react';
import { X, CheckCircle, Package, Grid, DollarSign, ShoppingCart, Plus, AlertTriangle } from 'lucide-react';
import ShareOptionsToolbar from './ShareOptionsToolbar';
import ConvertToSaleModal from './ConvertToSaleModal';
import AddressedPersonModal from './AddressedPersonModal';
import { sendQuotationWhatsApp, sendQuotationEmail, downloadQuotation, updateCalculationAddress } from '../../api/calculator';

const ViewCalculationModal = ({ 
  result, 
  isOpen, 
  onClose,
  onAddToQuotation,
  onConversionComplete,
  quotationId = null
}) => {
  const [showConvertModal, setShowConvertModal] = useState(false);
  const [addressedModal, setAddressedModal] = useState({ open: false, actionType: '', callback: null });

  if (!isOpen || !result) return null;

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const handlePrint = (addressedTo) => {
    const printWindow = window.open('', '_blank');
    
    // Build results HTML for multi-size or single
    const resultsHtml = result.results 
      ? result.results.map((r, i) => `
        <div style="margin-bottom: 15px; padding: 15px; background: #f5f5f5; border-radius: 8px;">
          <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px;">
            <h4 style="margin: 0; color: #1a1a2e;">${i + 1}. ${r.size}</h4>
            ${r.product_code ? `<span style="color: #D4AF37; font-weight: bold; font-family: monospace;">${r.product_code}</span>` : ''}
          </div>
          <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 8px;">
            <div><span style="color: #666;">Area:</span> <strong>${r.area_sqm} m²</strong></div>
            <div><span style="color: #666;">Tiles:</span> <strong>${r.tiles_needed}</strong></div>
            <div><span style="color: #666;">Cartons:</span> <strong>${r.cartons_needed}</strong></div>
            <div><span style="color: #666;">Coverage/Carton:</span> <strong>${r.coverage_per_carton} m²</strong></div>
            ${r.estimated_cost ? `<div><span style="color: #666;">Est. Cost:</span> <strong style="color: #D4AF37;">₦${parseFloat(r.estimated_cost).toLocaleString()}</strong></div>` : ''}
          </div>
        </div>
      `).join('')
      : `
        <div style="margin-bottom: 15px;">
          <div class="info-item"><span>Tiles Needed:</span><strong>${result.tiles_needed}</strong></div>
          <div class="info-item"><span>Cartons Needed:</span><strong>${result.cartons_needed}</strong></div>
        </div>
      `;
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Tile Calculation - ${result.calculation_number}</title>
          <style>
            body {
              font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
              padding: 40px;
              max-width: 800px;
              margin: 0 auto;
              color: #333;
            }
            .print-header {
              text-align: center;
              border-bottom: 2px solid #D4AF37;
              padding-bottom: 20px;
              margin-bottom: 30px;
            }
            .print-header h1 {
              color: #1a1a2e;
              margin: 0 0 10px 0;
            }
            .addressed-to {
              font-size: 1.3rem;
              margin-top: 15px;
              color: #1a1a2e;
            }
            .addressed-to span {
              color: #D4AF37;
              font-weight: bold;
            }
            .print-header .calc-number {
              color: #D4AF37;
              font-size: 1rem;
              font-weight: bold;
            }
            .section {
              margin-bottom: 25px;
            }
            .section h3 {
              color: #1a1a2e;
              border-bottom: 1px solid #ddd;
              padding-bottom: 8px;
              margin-bottom: 15px;
            }
            .info-item {
              display: flex;
              justify-content: space-between;
              padding: 8px 0;
              border-bottom: 1px dotted #eee;
            }
            .highlight-box {
              background: #f8f8f8;
              border: 2px solid #D4AF37;
              border-radius: 8px;
              padding: 20px;
              text-align: center;
              margin: 20px 0;
            }
            .highlight-box .main-value {
              font-size: 2.5rem;
              font-weight: bold;
              color: #D4AF37;
            }
            .footer {
              margin-top: 40px;
              padding-top: 20px;
              border-top: 1px solid #ddd;
              text-align: center;
              color: #666;
              font-size: 0.9rem;
            }
          </style>
        </head>
        <body>
          <div class="print-header">
            <h1>TILE CALCULATION</h1>
            <p class="addressed-to">for <span>${addressedTo}</span></p>
            <div class="calc-number">${result.calculation_number}</div>
            <div style="color: #666; margin-top: 10px;">
              ${new Date().toLocaleDateString('en-NG', { 
                year: 'numeric', month: 'long', day: 'numeric',
                hour: '2-digit', minute: '2-digit'
              })}
            </div>
          </div>

          <div class="section">
            <h3>Area Information</h3>
            <div class="info-item"><span>Total Area:</span><strong>${result.total_area_sqm} m²</strong></div>
            <div class="info-item"><span>Wastage Applied:</span><strong>${result.wastage_percent}%</strong></div>
          </div>

          <div class="section">
            <h3>Tile Requirements</h3>
            ${resultsHtml}
          </div>

          <div class="highlight-box">
            <div class="main-value">${result.total_cartons_needed || result.cartons_needed}</div>
            <div>Total Cartons Needed</div>
          </div>

          ${result.estimated_cost ? `
            <div class="section">
              <h3>Cost Estimation</h3>
              <div class="info-item"><span>Estimated Total:</span><strong>${formatCurrency(result.estimated_cost)}</strong></div>
            </div>
          ` : ''}

          <div class="footer">
            <p>This is a computer-generated calculation report.</p>
            <p>Valid for 7 days.</p>
          </div>
        </body>
      </html>
    `);
    
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  };

  const handleWhatsApp = async (phone, addressedTo) => {
    if (quotationId) {
      await sendQuotationWhatsApp(quotationId, phone);
    } else {
      // Generate WhatsApp message for direct calculation
      const text = generateCalculationText(result, addressedTo);
      const encodedText = encodeURIComponent(text);
      window.open(`https://wa.me/${phone}?text=${encodedText}`, '_blank');
    }
  };

  const handleEmail = async (email, addressedTo) => {
    if (quotationId) {
      await sendQuotationEmail(quotationId, email);
    } else {
      // Open email client for direct calculation
      const text = generateCalculationText(result, addressedTo);
      const subject = encodeURIComponent(`Tile Calculation for ${addressedTo} - ${result.calculation_number}`);
      const body = encodeURIComponent(text);
      window.open(`mailto:${email}?subject=${subject}&body=${body}`, '_blank');
    }
  };

  const handleDownload = async (addressedTo) => {
    if (quotationId) {
      await downloadQuotation(quotationId);
    } else {
      // Download as text file
      const text = generateCalculationText(result, addressedTo);
      const blob = new Blob([text], { type: 'text/plain' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Calculation-${addressedTo.replace(/\s+/g, '_')}-${result.calculation_number}.txt`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    }
  };

  const generateCalculationText = (calc, addressedTo) => {
    let text = `📋 TILE CALCULATION for ${addressedTo}\n`;
    text += `━━━━━━━━━━━━━━━━━━━━\n`;
    text += `Calculation #: ${calc.calculation_number}\n`;
    text += `Date: ${new Date().toLocaleDateString('en-NG')}\n\n`;
    
    text += `📐 AREA DETAILS\n`;
    text += `━━━━━━━━━━━━━━━━━━━━\n`;
    text += `Total Area: ${calc.total_area_sqm} m²\n`;
    text += `Wastage: ${calc.wastage_percent}%\n\n`;
    
    text += `📦 TILE REQUIREMENTS\n`;
    text += `━━━━━━━━━━━━━━━━━━━━\n`;
    
    if (calc.results && calc.results.length > 0) {
      calc.results.forEach((item, index) => {
        text += `\n${index + 1}. ${item.size}`;
        if (item.product_code) {
          text += ` (${item.product_code})`;
        }
        text += `\n`;
        text += `   Area: ${item.area_sqm} m²\n`;
        text += `   Tiles: ${item.tiles_needed}\n`;
        text += `   Cartons: ${item.cartons_needed}\n`;
        if (item.estimated_cost) {
          text += `   Est. Cost: ₦${parseFloat(item.estimated_cost).toLocaleString()}\n`;
        }
      });
    }
    
    text += `\n━━━━━━━━━━━━━━━━━━━━\n`;
    text += `TOTAL CARTONS: ${calc.total_cartons_needed || calc.cartons_needed}\n`;
    
    if (calc.estimated_cost) {
      text += `TOTAL ESTIMATE: ₦${parseFloat(calc.estimated_cost).toLocaleString()}\n`;
    }
    
    text += `\n✨ Thank you for choosing us!\n`;
    
    return text;
  };

  // Handle action with addressed person modal
  const handleBeforeAction = (actionType, callback) => {
    setAddressedModal({ open: true, actionType, callback });
  };

  const handleAddressedConfirm = async (addressedTo) => {
    // Update calculation with addressed person if it exists
    const calcId = result.calculation_id || result.id;
    if (calcId) {
      try {
        await updateCalculationAddress(calcId, addressedTo);
      } catch (error) {
        console.error('Failed to save addressed person:', error);
      }
    }

    setAddressedModal({ open: false, actionType: '', callback: null });
    if (addressedModal.callback) {
      addressedModal.callback(addressedTo);
    }
  };

  const handleConversionComplete = () => {
    setShowConvertModal(false);
    if (onConversionComplete) onConversionComplete();
  };

  // Check if this is a multi-size calculation
  const isMultiSize = result.results && result.results.length > 0;
  const totalCartons = result.total_cartons_needed || result.cartons_needed;
  const totalTiles = result.total_tiles_needed || result.tiles_needed;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-hidden animate-fade-in">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-secondary-lightGray">
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-2 px-3 py-1.5 bg-green-50 border border-green-200 rounded-full text-green-600 text-sm">
              <CheckCircle className="w-4 h-4" />
              Calculation Complete
            </div>
          </div>
          <button 
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 overflow-y-auto max-h-[calc(90vh-180px)]">
          {/* Calculation Number */}
          <div className="text-center mb-4">
            <span className="text-sm font-mono text-accent-gold">{result.calculation_number}</span>
            {result.title && (
              <h2 className="text-xl font-bold text-primary-black mt-1">{result.title}</h2>
            )}
          </div>

          {/* Area Info */}
          <div className="p-3 bg-secondary-offWhite rounded-lg mb-4 space-y-2">
            {result.input && (
              <div className="flex justify-between text-sm">
                <span className="text-primary-slate">Area Dimensions:</span>
                <span className="font-medium text-primary-black">
                  {result.input.area_length_m}m × {result.input.area_width_m}m
                </span>
              </div>
            )}
            <div className="flex justify-between text-sm">
              <span className="text-primary-slate">Total Area:</span>
              <span className="font-bold text-accent-gold">{result.total_area_sqm} m²</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-primary-slate">Wastage Applied:</span>
              <span className="font-medium text-primary-black">{result.wastage_percent}%</span>
            </div>
          </div>

          {/* Multi-Size Results */}
          {isMultiSize && (
            <div className="mb-4">
              <h4 className="text-sm font-semibold text-primary-black mb-2">Tile Breakdown</h4>
              {result.results.map((item, index) => (
                <div key={index} className="p-3 bg-secondary-offWhite rounded-lg mb-2">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-primary-black">{item.size}</span>
                    <div className="flex items-center gap-2">
                      {item.product_code && (
                        <span className="font-mono text-accent-gold font-bold text-sm">{item.product_code}</span>
                      )}
                      <span className="text-xs text-primary-slate">{item.tile_dimensions}</span>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-sm">
                    <div className="text-center">
                      <p className="font-bold text-primary-black">{item.area_sqm}</p>
                      <p className="text-xs text-primary-slate">m²</p>
                    </div>
                    <div className="text-center">
                      <p className="font-bold text-primary-black">{item.tiles_needed}</p>
                      <p className="text-xs text-primary-slate">Tiles</p>
                    </div>
                    <div className="text-center">
                      <p className="font-bold text-accent-gold">{item.cartons_needed}</p>
                      <p className="text-xs text-primary-slate">Cartons</p>
                    </div>
                  </div>
                  {item.estimated_cost && (
                    <div className="mt-2 pt-2 border-t border-secondary-lightGray text-right">
                      <span className="text-xs text-primary-slate">Est: </span>
                      <span className="font-bold text-accent-gold">{formatCurrency(item.estimated_cost)}</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Main Results */}
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div className="p-4 bg-secondary-offWhite border border-secondary-lightGray rounded-lg text-center">
              <Grid className="w-6 h-6 text-primary-slate mx-auto mb-2" />
              <p className="text-3xl font-bold text-primary-black">{totalTiles}</p>
              <p className="text-sm text-primary-slate">Tiles Needed</p>
            </div>
            <div className="p-4 bg-accent-gold/10 border border-accent-gold/30 rounded-lg text-center">
              <Package className="w-6 h-6 text-accent-gold mx-auto mb-2" />
              <p className="text-3xl font-bold text-accent-gold">{totalCartons}</p>
              <p className="text-sm text-primary-slate">Cartons to Buy</p>
            </div>
          </div>

          {/* Cost Estimation (if available) */}
          {result.estimated_cost && (
            <div className="p-4 bg-primary-black rounded-lg mb-4">
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-2 font-semibold text-white">
                  <DollarSign className="w-4 h-4" /> Estimated Total:
                </span>
                <span className="text-2xl font-bold text-accent-gold">{formatCurrency(result.estimated_cost)}</span>
              </div>
            </div>
          )}

          {/* Stock Warning */}
          {result.sufficient_stock === false && (
            <div className="flex items-start gap-3 p-3 bg-red-50 border border-red-200 rounded-lg mb-4 text-red-600">
              <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium">Insufficient Stock</p>
                <p className="text-sm">Stock check failed. Please verify availability.</p>
              </div>
            </div>
          )}

          {/* Share Options */}
          <ShareOptionsToolbar
            onPrint={handlePrint}
            onWhatsApp={handleWhatsApp}
            onEmail={handleEmail}
            onDownload={handleDownload}
            onBeforeAction={handleBeforeAction}
          />
        </div>

        {/* Footer Actions */}
        <div className="p-4 border-t border-secondary-lightGray bg-secondary-offWhite">
          <div className="grid grid-cols-2 gap-3">
            {onAddToQuotation && (
              <button 
                className="flex items-center justify-center gap-2 px-4 py-3 bg-white border border-secondary-lightGray text-primary-slate rounded-lg font-medium hover:bg-gray-50 transition-colors"
                onClick={() => {
                  onAddToQuotation(result);
                  onClose();
                }}
              >
                <Plus className="w-4 h-4" />
                Add to Quotation
              </button>
            )}
            <button 
              className="flex items-center justify-center gap-2 px-4 py-3 bg-accent-gold text-white rounded-lg font-semibold hover:bg-accent-goldDark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={() => setShowConvertModal(true)}
              disabled={result.sufficient_stock === false}
            >
              <ShoppingCart className="w-4 h-4" />
              Convert to Sale
            </button>
          </div>
        </div>

        {/* Convert Modal */}
        {showConvertModal && (
          <ConvertToSaleModal
            calculation={result}
            onClose={() => setShowConvertModal(false)}
            onSuccess={handleConversionComplete}
          />
        )}

        {/* Addressed Person Modal */}
        <AddressedPersonModal
          isOpen={addressedModal.open}
          onClose={() => setAddressedModal({ open: false, actionType: '', callback: null })}
          onConfirm={handleAddressedConfirm}
          actionType={addressedModal.actionType}
          documentType="calculation"
        />
      </div>
    </div>
  );
};

export default ViewCalculationModal;
